/* ═══════════════════════════════════════════════════════════════════
   GREEKSOFT API KNOWLEDGE BASE — Complete Reference Renderer
   Renders the full GREEKSOFT_API_COMPLETE_REFERENCE.md as styled HTML.
   Credentials are redacted: GREEK13 → [CLIENT_ID], Greek@123 → [REDACTED]
   ═══════════════════════════════════════════════════════════════════ */

/* ── Markdown → HTML renderer ── */

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[*`[\]()]/g, '')       // strip markdown formatting chars
    .replace(/[^\w\s-]/g, '')        // strip special characters
    .replace(/\s+/g, '-')            // spaces → dashes
    .replace(/-+/g, '-')             // collapse multiple dashes
    .replace(/^-|-$/g, '')           // trim leading/trailing dashes
}

function renderMarkdown(md: string): string {
  const lines = md.split('\n')
  const html: string[] = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]

    // Code blocks (triple backtick)
    if (line.trimStart().startsWith('```')) {
      const lang = line.trim().slice(3).trim()
      i++
      const codeLines: string[] = []
      while (i < lines.length && !lines[i].trimStart().startsWith('```')) {
        codeLines.push(lines[i])
        i++
      }
      i++ // skip closing ```
      const escaped = escapeHtml(codeLines.join('\n'))
      const langClass = lang ? ` class="language-${escapeHtml(lang)}"` : ''
      const langLabel = lang ? `<span class="api-code-lang">${escapeHtml(lang)}</span>` : ''
      html.push(`<div class="api-code-block">${langLabel}<pre><code${langClass}>${escaped}</code></pre></div>`)
      continue
    }

    // Horizontal rule
    if (/^---+\s*$/.test(line.trim())) {
      html.push('<hr />')
      i++
      continue
    }

    // Headers
    if (line.startsWith('####')) {
      const text = line.slice(4).trim()
      html.push(`<h4 id="${slugify(text)}">${renderInline(text)}</h4>`)
      i++
      continue
    }
    if (line.startsWith('###')) {
      const text = line.slice(3).trim()
      html.push(`<h3 id="${slugify(text)}">${renderInline(text)}</h3>`)
      i++
      continue
    }
    if (line.startsWith('##')) {
      const text = line.slice(2).trim()
      html.push(`<h2 id="${slugify(text)}">${renderInline(text)}</h2>`)
      i++
      continue
    }
    if (line.startsWith('# ')) {
      const text = line.slice(2).trim()
      html.push(`<h1 id="${slugify(text)}">${renderInline(text)}</h1>`)
      i++
      continue
    }

    // Blockquote
    if (line.trimStart().startsWith('>')) {
      const bqLines: string[] = []
      while (i < lines.length && lines[i].trimStart().startsWith('>')) {
        bqLines.push(lines[i].replace(/^\s*>\s?/, ''))
        i++
      }
      html.push(`<blockquote>${renderInline(bqLines.join(' '))}</blockquote>`)
      continue
    }

    // Table
    if (line.includes('|') && line.trim().startsWith('|')) {
      const tableRows: string[] = []
      while (i < lines.length && lines[i].includes('|') && lines[i].trim().startsWith('|')) {
        tableRows.push(lines[i])
        i++
      }
      html.push(renderTable(tableRows))
      continue
    }

    // Unordered list
    if (/^\s*[-*]\s/.test(line)) {
      const listItems: string[] = []
      while (i < lines.length && /^\s*[-*]\s/.test(lines[i])) {
        listItems.push(lines[i].replace(/^\s*[-*]\s+/, ''))
        i++
      }
      html.push('<ul>' + listItems.map(li => `<li>${renderInline(li)}</li>`).join('') + '</ul>')
      continue
    }

    // Ordered list
    if (/^\s*\d+\.\s/.test(line)) {
      const listItems: string[] = []
      while (i < lines.length && /^\s*\d+\.\s/.test(lines[i])) {
        listItems.push(lines[i].replace(/^\s*\d+\.\s+/, ''))
        i++
      }
      html.push('<ol>' + listItems.map(li => `<li>${renderInline(li)}</li>`).join('') + '</ol>')
      continue
    }

    // Empty line
    if (line.trim() === '') {
      i++
      continue
    }

    // Paragraph — collect consecutive non-empty, non-special lines
    const pLines: string[] = []
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !lines[i].trimStart().startsWith('```') &&
      !lines[i].trimStart().startsWith('#') &&
      !lines[i].trimStart().startsWith('>') &&
      !/^---+\s*$/.test(lines[i].trim()) &&
      !(lines[i].includes('|') && lines[i].trim().startsWith('|')) &&
      !/^\s*[-*]\s/.test(lines[i]) &&
      !/^\s*\d+\.\s/.test(lines[i])
    ) {
      pLines.push(lines[i])
      i++
    }
    if (pLines.length > 0) {
      html.push(`<p>${renderInline(pLines.join(' '))}</p>`)
    }
  }

  return html.join('\n')
}

function renderInline(text: string): string {
  // Escape HTML first but preserve some constructs
  let s = text
  // Bold **text**
  s = s.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
  // Inline code `text`
  s = s.replace(/`([^`]+)`/g, (_m, code) => `<code>${escapeHtml(code)}</code>`)
  // Links [text](url) — anchor links scroll in-page, external links open new tab
  s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, linkText, url) => {
    if (url.startsWith('#')) {
      return `<a href="${url}" style="cursor:pointer">${linkText}</a>`
    }
    return `<a href="${url}" target="_blank" rel="noopener">${linkText}</a>`
  })
  return s
}

function renderTable(rows: string[]): string {
  const parseRow = (row: string): string[] =>
    row
      .split('|')
      .slice(1, -1)
      .map(cell => cell.trim())

  if (rows.length < 2) return ''

  const headers = parseRow(rows[0])
  // Skip separator row (row[1])
  const bodyRows = rows.slice(2)

  let t = '<div class="api-table-wrap"><table><thead><tr>'
  for (const h of headers) {
    t += `<th>${renderInline(h)}</th>`
  }
  t += '</tr></thead><tbody>'
  for (const row of bodyRows) {
    const cells = parseRow(row)
    t += '<tr>'
    for (let c = 0; c < headers.length; c++) {
      t += `<td>${renderInline(cells[c] || '')}</td>`
    }
    t += '</tr>'
  }
  t += '</tbody></table></div>'
  return t
}

/* ── Styles for the API doc renderer ── */

const apiDocStyles = `
  .api-doc { max-width: 100%; }

  .api-doc h1 {
    font-size: 1.5rem; font-weight: 900; margin: 2rem 0 1rem;
    color: var(--text-primary);
    padding-bottom: 0.5rem;
    border-bottom: 2px solid var(--accent);
  }

  .api-doc h2 {
    font-size: 1.25rem; font-weight: 800; margin: 2.5rem 0 0.75rem;
    color: var(--text-primary);
    padding: 0.5rem 0 0.5rem 0.875rem;
    border-left: 4px solid var(--accent);
    background: linear-gradient(90deg, rgba(56,189,248,0.06) 0%, transparent 100%);
    border-radius: 0 6px 6px 0;
  }

  .api-doc h3 {
    font-size: 1.0625rem; font-weight: 700; margin: 1.75rem 0 0.5rem;
    color: var(--text-primary);
    padding-left: 0.625rem;
    border-left: 3px solid var(--purple);
  }

  .api-doc h4 {
    font-size: 0.9375rem; font-weight: 700; margin: 1.25rem 0 0.375rem;
    color: var(--accent);
  }

  .api-doc p {
    font-size: 0.8125rem; line-height: 1.8; color: var(--text-secondary);
    margin: 0.375rem 0 0.75rem;
  }

  .api-doc strong {
    color: var(--text-primary); font-weight: 700;
  }

  .api-doc a {
    color: var(--accent); text-decoration: underline;
    text-underline-offset: 2px;
  }
  .api-doc a:hover { color: var(--purple); }

  .api-doc code {
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-size: 0.75rem;
    background: rgba(56,189,248,0.08);
    color: var(--accent);
    padding: 0.1rem 0.375rem;
    border-radius: 4px;
    border: 1px solid rgba(56,189,248,0.12);
  }

  .api-doc .api-code-block {
    position: relative;
    margin: 0.75rem 0 1rem;
  }

  .api-doc .api-code-lang {
    position: absolute; top: 0; right: 0;
    font-size: 0.5625rem; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.06em;
    padding: 0.2rem 0.625rem;
    background: rgba(56,189,248,0.15);
    color: var(--accent);
    border-radius: 0 8px 0 6px;
    z-index: 1;
  }

  .api-doc pre {
    background: rgba(0,0,0,0.4);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 1rem 1.25rem;
    overflow-x: auto;
    margin: 0;
  }

  .api-doc pre code {
    background: none; border: none; padding: 0;
    font-size: 0.75rem; line-height: 1.65;
    color: var(--text-secondary);
  }

  .api-doc .api-table-wrap {
    overflow-x: auto;
    margin: 0.75rem 0 1rem;
    border-radius: 8px;
    border: 1px solid var(--border);
  }

  .api-doc table {
    width: 100%; border-collapse: collapse;
    font-size: 0.75rem;
  }

  .api-doc th {
    text-align: left;
    padding: 0.5rem 0.75rem;
    background: rgba(56,189,248,0.06);
    color: var(--text-primary);
    font-weight: 700;
    font-size: 0.6875rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border-bottom: 1px solid var(--border);
    white-space: nowrap;
  }

  .api-doc td {
    padding: 0.4rem 0.75rem;
    color: var(--text-secondary);
    border-bottom: 1px solid rgba(255,255,255,0.04);
    vertical-align: top;
  }

  .api-doc tr:last-child td { border-bottom: none; }

  .api-doc tr:hover td {
    background: rgba(56,189,248,0.03);
  }

  .api-doc blockquote {
    margin: 0.75rem 0;
    padding: 0.625rem 1rem;
    border-left: 3px solid var(--accent);
    background: rgba(56,189,248,0.04);
    border-radius: 0 6px 6px 0;
    font-size: 0.8125rem;
    line-height: 1.65;
    color: var(--text-secondary);
    font-style: italic;
  }

  .api-doc blockquote strong {
    color: var(--accent);
    font-style: normal;
  }

  .api-doc hr {
    border: none;
    height: 1px;
    background: var(--border);
    margin: 1.75rem 0;
  }

  .api-doc ul, .api-doc ol {
    margin: 0.5rem 0 0.75rem 1.25rem;
    padding: 0;
  }

  .api-doc li {
    font-size: 0.8125rem; line-height: 1.75;
    color: var(--text-secondary);
    margin-bottom: 0.25rem;
  }

  .api-doc li code {
    font-size: 0.6875rem;
  }

  .api-doc img {
    max-width: 100%;
    border-radius: 8px;
    margin: 0.5rem 0;
  }
`

/* ── The complete API document as a raw string (credentials redacted) ── */

const API_DOC_CONTENT = `# GREEKSOFT REST API — Complete Reference for SVIS (Shri Vijayshree Investment Services Pvt Ltd)

> **Broker**: SVIS (Shri Vijayshree Investment Services Pvt Ltd)
> **Client ID**: \`[CLIENT_ID]\` | **User Type**: PRO (Dealer)
> **Document Version**: 1.0 | **Last Updated**: 2026-05-30

---

## Table of Contents

1. [Authentication & Login Flow](#part-1-authentication--login-flow)
2. [Server Configuration](#part-2-server-configuration)
3. [Contracts & Instruments](#part-3-contracts--instruments)
4. [Market Data (REST)](#part-4-market-data-rest)
5. [Market Data (WebSocket — Apollo)](#part-5-market-data-websocket--apollo)
6. [Orders](#part-6-orders)
7. [Order Status](#part-7-order-status)
8. [Portfolio](#part-8-portfolio)
9. [Watchlists](#part-9-watchlists)
10. [Complete Field Reference](#part-10-complete-field-reference)
11. [Operational Rules](#part-11-operational-rules)
12. [Server Configuration (CTCLManager.ini)](#part-12-server-configuration-ctclmanagerini)
13. [Admin Setup & Support](#part-13-admin-setup--support)
14. [Complete Endpoint Inventory](#part-14-complete-endpoint-inventory)
15. [Architecture Diagram](#part-15-architecture-diagram)
16. [Environment Configuration](#part-16-environment-configuration)

---

## SVIS Broker-Specific Configuration

| Parameter | Value |
|-----------|-------|
| Auth Server | \`http://greekapi.greeksoft.in:3001\` |
| REST API (Arachne) | \`http://vpn.greeksoft.in:8230\` |
| WebSocket IRIS (Orders) | \`ws://vpn.greeksoft.in:8228\` |
| WebSocket Apollo (Market Data) | \`ws://vpn.greeksoft.in:8229\` |
| Client ID | \`[CLIENT_ID]\` |
| Session Password | \`[REDACTED]\` |
| CTCL Password | **PENDING** (to be obtained from SVIS broker) |
| User Type | PRO (Dealer) |
| iprocli | \`"2"\` (Dealer/PRO) |
| Broker ID | \`1\` |
| Version No | \`1.0.1.10\` |

---

## PART 1: Authentication & Login Flow

### Complete Login Flow (7 Steps)

\`\`\`mermaid
graph TD
    A["1. POST /auth/greek/sessiontoken"] -->|sessionToken JWT| B["2. POST /getFlagValues"]
    B -->|Server config, heartbeat intervals| C["3. POST /jloginNew"]
    C -->|sessionId, gcid, AllowedMarket| D{"4. isMPINSet?"}
    D -->|true| E["4a. POST /validateMPIN"]
    D -->|false| F["5. POST /getLoginInfo"]
    E --> F
    F -->|gcid (dynamic)| G["6. Start REST Heartbeat (jheartbeat)"]
    G --> H["7a. IRIS WebSocket Login"]
    G --> I["7b. Apollo WebSocket Login"]
    H --> J["Ready for Trading"]
    I --> J
\`\`\`

---

### Step 1: Get Session Token

\`\`\`
POST http://greekapi.greeksoft.in:3001/auth/greek/sessiontoken
\`\`\`

**Request Body:**

\`\`\`json
{
    "username": "[CLIENT_ID]",
    "password": "[REDACTED]",
    "validFor": "1d"
}
\`\`\`

**Response (201):**

\`\`\`json
{
    "id": 162,
    "sessionToken": "eyJhbGciOiJSUzI1NiIs..."
}
\`\`\`

**Notes:**

- Token is a JWT signed with RS256.
- Token stored as \`{{sessionToken}}\` — used in \`Authorization\` header for ALL subsequent calls.
- Default expiry = 4 hours. \`validFor\` field overrides this (e.g., \`"1d"\`, \`"30d"\`).
- **CRITICAL**: Generating a new session token will **invalidate the previous one**. Only ONE active session token per user at a time.
- If token is expired or invalid → 401 UnAuthorized Error response.
- JWT payload includes \`message_rate\` field indicating rate limit.

---

### Step 2: Get Flag Values

\`\`\`
POST http://vpn.greeksoft.in:8230/getFlagValues
Authorization: {{sessionToken}}
\`\`\`

**Request Body:**

\`\`\`json
{
    "request": {
        "svcVersion": "1.0.0",
        "svcGroup": "",
        "svcName": "getFlagValues",
        "gscid": "",
        "assetType": "",
        "data": {}
    }
}
\`\`\`

**Response Fields:**

| Field | Description |
|-------|-------------|
| \`Apollo_IP\` | Apollo WebSocket server IP |
| \`Apollo_Port\` | Apollo WebSocket server port |
| \`Arachne_IP\` | REST API server IP |
| \`Arachne_Port\` | REST API server port |
| \`Iris_IP\` | IRIS WebSocket server IP |
| \`Iris_Port\` | IRIS WebSocket server port |
| \`BroadcastSender_Port\` | Broadcast sender port |
| \`OrderSender_Port\` | Order sender port |
| \`heartbeat_Intervals\` | Heartbeat frequency (default 10 seconds) |
| \`IsRedisEnabled\` | Redis cache status |
| \`IsStrategyProduct\` | Strategy product availability |
| \`IsEDISProduct\` | EDIS product availability |
| \`IsPledgeProduct\` | Pledge product availability |
| \`DPType\` | Depository participant type |
| \`PaymentGateway\` | Payment gateway (e.g., "atom") |
| \`upiPaymentEnabled\` | UPI payment support |
| \`ft_Link\` | Fund transfer link |
| \`ft_Link_Upi\` | Fund transfer UPI link |
| \`ChartSetting\` | Chart configuration |
| \`reconnection_attempts\` | Max reconnection attempts |
| \`validate2FA\` | Two-factor authentication flag |
| \`ssl_url\` | SSL configuration URL |

**Field Classification:**

| Classification | Fields |
|---------------|--------|
| Mandatory | \`svcName\`, \`svcGroup\` (but works if blank/invalid) |
| Non-mandatory | \`svcVersion\`, \`assetType\`, \`gscid\`, \`data\` |

---

### Step 3: Login (jloginNew)

\`\`\`
POST http://vpn.greeksoft.in:8230/jloginNew
Authorization: {{sessionToken}}
\`\`\`

**Request Body:**

\`\`\`json
{
  "request": {
    "data": {
      "pan_dob": "01/01/1901",
      "deviceId": "",
      "gscid": "[CLIENT_ID]",
      "deviceDetails": "",
      "deviceType": "0",
      "pass": "<CTCL_PASSWORD_MD5_HASH>",
      "transPass": "",
      "userType": "Customer",
      "brokerid": "1",
      "passType": "0",
      "version_no": "1.0.1.10",
      "encryptionType": "1"
    },
    "svcName": "jloginNew",
    "svcGroup": "Login"
  }
}
\`\`\`

**Field Classification:**

| Classification | Fields |
|---------------|--------|
| Mandatory | \`gscid\`, \`pass\`, \`brokerid\`, \`version_no\`, \`svcName\`, \`svcGroup\` |
| Non-mandatory | \`pan_dob\`, \`deviceId\`, \`deviceDetails\`, \`deviceType\`, \`transPass\`, \`userType\`, \`passType\`, \`encryptionType\` |

**Password Notes:**

- CTCL password must be MD5 hashed before sending in \`pass\` field.
- The server silently fails with blank/invalid passwords (no error response).
- CTCL password pending — obtain from SVIS broker.

**Response Fields:**

\`gcid\`, \`gscid\`, \`sessionId\`, \`AllowedMarket[]\`, \`clientName\`, \`panNo\`, \`KYCStatus\`, \`userType\`, \`isMPINSet\`, \`Theme\`, \`quote\`, \`Apollo_IP\`, \`Apollo_Port\`, \`Arachne_IP\`, \`Arachne_Port\`, \`Iris_IP\`, \`Iris_Port\`, \`BroadcastSender_Port\`, \`OrderSender_Port\`

**jloginNew Error Codes:**

| Code | Message |
|------|---------|
| 1 | Password Expired |
| 2 | Invalid Password |
| 3 | Failure |
| 4 | Duplicate Password not Allowed |
| 5 | Max Attempts Exceeded for wrong Password |
| 6 | Inactive User |
| 7 | Inactive User |
| 8 | Invalid 2FA Answer |
| 9 | Same Id password |
| 10 | Same Login and Transaction passwords |
| 11 | Guest not registered |
| 12 | Guest already registered |
| 13 | Retailer does not exist |
| 14 | Version mismatch |
| 17 | Account Locked, Please Contact Admin and Change Password |
| 18 | Login & Transaction Password Expired |

> **Note:** Codes 15 and 16 are not defined (gap in numbering).

---

### Step 4: Validate MPIN (if required)

\`\`\`
POST http://vpn.greeksoft.in:8230/validateMPIN
Authorization: {{sessionToken}}
\`\`\`

**Request Body:**

\`\`\`json
{
  "request": {
    "data": {
      "gscid": "[CLIENT_ID]",
      "mpin": "<USER_MPIN>"
    },
    "svcName": "validateMPIN",
    "svcGroup": "Login"
  }
}
\`\`\`

**Response:**

\`\`\`json
{
  "ErrorCode": 0,
  "data": {
    "ErrorCode": 0
  }
}
\`\`\`

**Notes:**

- Only required when \`isMPINSet: "true"\` in jloginNew response.
- MPIN is user-created.

---

### Step 5: Get Login Info

\`\`\`
POST http://vpn.greeksoft.in:8230/getLoginInfo
Authorization: {{sessionToken}}
\`\`\`

**Request Body:**

\`\`\`json
{
    "request": {
        "svcVersion": "1.0.0",
        "svcGroup": "Login",
        "svcName": "getLoginInfo",
        "assetType": "",
        "data": {
            "gscid": "[CLIENT_ID]"
        }
    }
}
\`\`\`

**Field Classification:**

| Classification | Fields |
|---------------|--------|
| Mandatory | \`gscid\` (must be CAPITAL letters), \`svcName\`, \`svcGroup\` |
| Non-mandatory | \`svcVersion\`, \`assetType\` |

**Response Fields:**

\`gcid\` (dynamic — changes per session), \`gscid\`, \`AllowedMarket[].market_id\`

> **CRITICAL**: The GCID received from GetLoginInfo is **not constant** and may change between sessions. Must fetch fresh each session.

---

### Step 6: REST Heartbeat (Jheartbeat)

\`\`\`
POST http://vpn.greeksoft.in:8230/jheartbeat
Authorization: {{sessionToken}}
\`\`\`

**Request Body:**

\`\`\`json
{
  "request": {
    "data": {
      "gscid": "[CLIENT_ID]",
      "sessionId": "<sessionId_from_jloginNew>"
    },
    "svcName": "jheartbeat",
    "svcGroup": "jheartbeat"
  }
}
\`\`\`

**Rules:**

- Send every \`heartbeat_Intervals\` seconds (default: 10).
- If heartbeat response not received 3 times then Login request must be sent again.
- If heartbeat response has error code other than 0, user gets logged out.
- This is a REST heartbeat to Arachne — SEPARATE from WebSocket heartbeats.

---

### Step 7: WebSocket Connections

#### 7a. IRIS WebSocket Login (Orders)

\`\`\`
ws://vpn.greeksoft.in:8228
\`\`\`

**Login Message:**

\`\`\`json
{
  "request": {
    "data": {
      "gscid": "[CLIENT_ID]",
      "gcid": "<gcid_from_getLoginInfo>",
      "sessionId": "<sessionId_from_jloginNew>",
      "device_type": "0"
    },
    "response_format": "json",
    "request_type": "subscribe",
    "streaming_type": "login"
  }
}
\`\`\`

**IRIS Field Classification:**

| Classification | Fields | Notes |
|---------------|--------|-------|
| Mandatory | \`gscid\` | No rejection if blank |
| Mandatory | \`gcid\` | Required |
| Mandatory | \`sessionId\` | Required |
| Mandatory | \`request_type\` | No rejection if blank |
| Mandatory | \`streaming_type\` | No rejection if blank |
| Non-mandatory | \`device_type\` | — |
| Non-mandatory | \`response_format\` | — |

#### 7b. Apollo WebSocket Login (Market Data)

\`\`\`
ws://vpn.greeksoft.in:8229
\`\`\`

**Login Message:**

\`\`\`json
{
  "request": {
    "data": {
      "gscid": "[CLIENT_ID]",
      "gcid": "<gcid_from_getLoginInfo>",
      "sessionId": "<sessionId_from_jloginNew>",
      "device_type": "0"
    },
    "response_format": "json",
    "request_type": "subscribe",
    "streaming_type": "login"
  }
}
\`\`\`

**Apollo Field Classification:**

| Classification | Fields | Notes |
|---------------|--------|-------|
| Mandatory | \`gcid\` | Required |
| Mandatory | \`sessionId\` | Required |
| Mandatory | \`request_type\` | Required |
| Mandatory | \`streaming_type\` | Required |
| Non-mandatory | \`gscid\` | Validation pending |
| Non-mandatory | \`device_type\` | — |
| Non-mandatory | \`response_format\` | — |

#### WebSocket Heartbeat (both IRIS & Apollo)

\`\`\`json
{
  "request": {
    "data": {
      "gcid": "<gcid>",
      "sessionId": "<sessionId>"
    },
    "response_format": "json",
    "request_type": "subscribe",
    "streaming_type": "HeartBeat"
  }
}
\`\`\`

**IRIS Heartbeat Field Classification:**

| Classification | Fields | Notes |
|---------------|--------|-------|
| Mandatory | \`request_type\` | Required |
| Mandatory | \`streaming_type\` | Required |
| Non-mandatory | \`gcid\` | Validation pending |
| Non-mandatory | \`sessionId\` | Validation pending |
| Non-mandatory | \`response_format\` | — |

> **WebSocket timeout: 15-minute inactivity** — if no data sent for 15 minutes, WebSocket connection is terminated. Heartbeat is mandatory on both WebSockets.

---

## PART 2: Server Configuration

### Get Allowed Products

\`\`\`
POST http://vpn.greeksoft.in:8230/getAllowedProduct
Authorization: {{sessionToken}}
\`\`\`

**Request Body:**

\`\`\`json
{
    "request": {
        "data": {},
        "svcName": "getAllowedProduct",
        "svcGroup": "Login"
    }
}
\`\`\`

**Field Classification:**

| Classification | Fields | Notes |
|---------------|--------|-------|
| Mandatory | \`svcName\`, \`svcGroup\` | Works if blank/invalid |
| Non-mandatory | \`data\` | — |

**Product Tokens:**

| Token | Product |
|-------|---------| 
| 0 | DELIVERY |
| 1 | INTRADAY |
| 2 | MTF |
| 3 | TNC |
| 4 | CATALYST |
| 5 | SSEQ |

---

## PART 3: Contracts & Instruments

### Get All Contracts

\`\`\`
GET http://vpn.greeksoft.in:8230/getAllContract
Authorization: {{sessionToken}}
\`\`\`

---

### Get Scrip Details (Equity/Future)

\`\`\`
GET http://vpn.greeksoft.in:8230/getFullScripDetailsBySymbol_Mobile?exchange=NSE&assetType=equity&code=<Base64>&type=equity
Authorization: {{sessionToken}}
\`\`\`

**Parameters:**

| Param | Values | Description |
|-------|--------|-------------|
| \`exchange\` | \`NSE\`, \`BSE\` | Exchange |
| \`assetType\` | \`future\`, \`option\`, \`equity\`, \`currency\`, \`commodity\` | Asset type |
| \`code\` | e.g. \`UkVMSUFOQ0U=\` (Base64 of "RELIANCE") | Symbol code (Base64 encoded) |
| \`type\` | \`equity\`, \`future\`, \`calloption\`, \`putoption\` | Instrument type |

**Asset Type to Type Parameter Mapping:**

| \`assetType\` | \`type\` parameter | Instruments |
|-------------|-----------------|-------------|
| \`equity\` | \`equity\` | Cash stocks |
| \`option\` | \`calloption\` | Call options |
| \`option\` | \`putoption\` | Put options |
| \`future\` | \`future\` | Futures |
| \`currency\` | \`future\` | Currency futures |
| \`currency\` | \`calloption\` | Currency call options |
| \`currency\` | \`putoption\` | Currency put options |
| \`commodity\` | \`calloption\` | Commodity call options |
| \`commodity\` | \`putoption\` | Commodity put options |

> **Note:** The \`code\` parameter must be Base64 encoded (e.g., \`RELIANCE\` → \`UkVMSUFOQ0U=\`, \`nifty\` → \`bmlmdHk=\`).

---

### Get Option Chain

\`\`\`
GET http://vpn.greeksoft.in:8230/getFullScripDetailsBySymbol_Option?exchange=NSE&assetType=future&code=<Base64>&type=calloption&expiry=<timestamp>
Authorization: {{sessionToken}}
\`\`\`

---

### Get Expiry Dates

\`\`\`
GET http://vpn.greeksoft.in:8230/getFullScripDetailsBySymbol_Expiry?exchange=NSE&assetType=future&code=<Base64>&type=calloption
Authorization: {{sessionToken}}
\`\`\`

**Response:**

\`\`\`json
{
  "data": [
    {"expiry": 1611824400},
    {"expiry": 1614243600},
    {"expiry": 1616662800}
  ]
}
\`\`\`

---

## PART 4: Market Data (REST)

### Market Status

\`\`\`
POST http://vpn.greeksoft.in:8230/getMarketStatus
Authorization: {{sessionToken}}
\`\`\`

**Request Body:**

\`\`\`json
{
    "request": {
        "data": {
            "gscid": "[CLIENT_ID]"
        },
        "svcName": "getMarketStatus",
        "svcGroup": "Markets"
    }
}
\`\`\`

**Field Classification:**

| Classification | Fields | Notes |
|---------------|--------|-------|
| Mandatory | \`gscid\` | Required |
| Mandatory | \`svcName\`, \`svcGroup\` | Work if blank/invalid |

**Market IDs:**

| ID | Exchange Segment |
|----|-----------------|
| 1 | NSE Equity |
| 2 | NSE F&O |
| 3 | NSE Currency |
| 4 | BSE Equity |
| 5 | BSE F&O |
| 6 | BSE Currency |

**Session Codes:**

| Code | Session State |
|------|---------------|
| 0 | PRE OPEN |
| 1 | OPEN |
| 2 | CLOSE |
| 3 | PRE OPEN CLOSE |
| -1 | DISCONNECTED |

**Login Status:**

| Code | Meaning |
|------|---------|
| 1 | Logged in |
| -1 | Logged off |

---

### Quote for Single Symbol

\`\`\`
POST http://vpn.greeksoft.in:8230/getQuoteForSingleSymbol_V2
Authorization: {{sessionToken}}
\`\`\`

**Request Body:**

\`\`\`json
{
    "request": {
        "data": {
            "token": "101002885",
            "assetType": "equity",
            "gscid": "[CLIENT_ID]",
            "gcid": "{{gcid}}"
        },
        "svcName": "getQuoteForSingleSymbol_V2",
        "svcGroup": "Markets"
    }
}
\`\`\`

**Field Classification:**

| Classification | Fields | Notes |
|---------------|--------|-------|
| Mandatory | \`token\` | Required |
| Mandatory | \`svcName\`, \`svcGroup\` | Work if blank/invalid |
| Non-mandatory | \`assetType\` | Validation pending |
| Non-mandatory | \`gscid\` | Validation pending |
| Non-mandatory | \`gcid\` | Validation pending |

---

### Quote for Multiple Symbols

\`\`\`
POST http://vpn.greeksoft.in:8230/getQuoteForMultipleSymbols
Authorization: {{sessionToken}}
\`\`\`

**Request Body:**

\`\`\`json
{
    "request": {
        "data": {
            "symbolList": [
                {
                    "assetType": "equity",
                    "exchange": "NSE",
                    "token": "101011536"
                },
                {
                    "assetType": "equity",
                    "exchange": "NSE",
                    "token": "101002885"
                }
            ]
        },
        "svcName": "getQuoteForMultipleSymbols",
        "svcGroup": "Markets"
    }
}
\`\`\`

**Field Classification:**

| Classification | Fields | Notes |
|---------------|--------|-------|
| Mandatory | \`exchange\` (per symbol) | Required |
| Mandatory | \`token\` (per symbol) | Required |
| Non-mandatory | \`assetType\` (per symbol) | — |
| Non-mandatory | \`svcName\`, \`svcGroup\` | Work if blank/invalid |

---

### Indian Indices Data

\`\`\`
GET http://vpn.greeksoft.in:8230/getIndianIndicesData_V2
Authorization: {{sessionToken}}
\`\`\`

---

### Market-by-Price Data

\`\`\`
GET http://vpn.greeksoft.in:8230/getToken_OnlyMbpData
Authorization: {{sessionToken}}
\`\`\`

**Request Body:**

\`\`\`json
{
    "request": {
        "data": {
            "mode": "FULL",
            "exchangeTokens": [
                {
                    "exchange": "NSE",
                    "token": "101001594"
                },
                {
                    "exchange": "NSE",
                    "token": "102059456"
                }
            ]
        },
        "svcName": "getToken_OnlyMbpData",
        "svcGroup": "Markets"
    }
}
\`\`\`

---

### OHLC Chart Data (Historical)

\`\`\`
POST http://vpn.greeksoft.in:8230/get_ohlc
Authorization: {{sessionToken}}
\`\`\`

**Request Body:**

\`\`\`json
{
  "request": {
    "FormFactor": "M",
    "data": {
      "gscid": "[CLIENT_ID]",
      "token": "101003918",
      "interval": "20",
      "date": "20240703",
      "time": 0,
      "noofdays": 1
    },
    "svcGroup": "portfolio",
    "svcVersion": "1.0.0",
    "svcName": "jhistorical_New",
    "requestType": "U"
  }
}
\`\`\`

**Field Classification:**

| Classification | Fields | Notes |
|---------------|--------|-------|
| Mandatory | \`gscid\` | Required |
| Mandatory | \`token\` | Required |
| Mandatory | \`noofdays\` | Required |
| Mandatory | \`date\` | Required |
| Non-mandatory | \`interval\` | Candle timeframe in minutes |
| Non-mandatory | \`time\` | — |
| Non-mandatory | \`FormFactor\` | — |
| Non-mandatory | \`requestType\` | — |

---

## PART 5: Market Data (WebSocket — Apollo)

### Subscribe to Market Picture (Full Depth)

\`\`\`
ws://vpn.greeksoft.in:8229
\`\`\`

**Subscribe Message:**

\`\`\`json
{
  "request": {
    "data": {
      "symbols": [
        {"symbol": "101002885"}
      ]
    },
    "response_format": "json",
    "gscid": "[CLIENT_ID]",
    "gcid": "{{gcid}}",
    "request_type": "subscribe",
    "streaming_type": "marketPicture"
  }
}
\`\`\`

**Field Classification:**

| Classification | Fields | Notes |
|---------------|--------|-------|
| Mandatory | \`data.symbols[].symbol\` | Required |
| Mandatory | \`request_type\` | Required |
| Mandatory | \`streaming_type\` | Required |
| Non-mandatory | \`gscid\` | Validation pending |
| Non-mandatory | \`gcid\` | Validation pending |
| Non-mandatory | \`response_format\` | — |

**Market Picture Response Fields:**

| Field | Type | Example | Description |
|-------|------|---------|-------------|
| \`ltp\` | string | \`"1188.85"\` | Last traded price |
| \`open\` | string | \`"1201.25"\` | Open price |
| \`high\` | string | \`"1216.35"\` | High price |
| \`low\` | string | \`"1184.40"\` | Low price |
| \`close\` | string | \`"1151.15"\` | Previous close |
| \`change\` | string | \`"37.70"\` | Absolute change |
| \`p_change\` | string | \`"3.27"\` | Percentage change |
| \`atp\` | string | \`"1193.87"\` | Average traded price |
| \`tot_vol\` | string | \`"2147846"\` | Total volume |
| \`oi\` | string | \`"0"\` | Open interest |
| \`bid\` | string | \`"1188.8000"\` | Best bid price |
| \`ask\` | string | \`"1189.20"\` | Best ask price |
| \`bidqty\` | string | \`"23"\` | Best bid qty |
| \`askqty\` | string | \`"10"\` | Best ask qty |
| \`tbq\` | string | \`"230334"\` | Total buy qty |
| \`tot_buyQty\` | string | \`"230334.00"\` | Total buy qty (decimal) |
| \`tot_sellQty\` | string | \`"347154.00"\` | Total sell qty |
| \`ltq\` | string | \`"6"\` | Last traded qty |
| \`l52w\` | string | \`"0.00"\` | 52-week low |
| \`h52w\` | string | \`"0.00"\` | 52-week high |
| \`ltt\` | string | \`"14-10-2022 14:09:00"\` | Last trade time |
| \`lut\` | string | \`"14-10-2022 14:09:02"\` | Last update time |
| \`symbol\` | string | \`"101005258"\` | Token |
| \`name\` | string | \`"INDUSINDBK"\` | Symbol name |
| \`exch\` | string | \`"NSE"\` | Exchange |
| \`asset_type\` | string | \`""\` | Asset type |
| \`level2\` | array | 5-depth bid/ask | Market depth |

**Level 2 (Market Depth) — 5 levels:**

\`\`\`json
{
  "bid": {"price": "1188.80", "no": "1", "qty": "23"},
  "ask": {"price": "1189.20", "no": "1", "qty": "10"}
}
\`\`\`

> **Note:** All values in broadcast responses are **strings** (including numbers).

---

### Subscribe to LTP Info (Lightweight)

\`\`\`json
{
  "request": {
    "data": {
      "symbols": [
        {"symbol": "101017963"},
        {"symbol": "101011915"},
        {"symbol": "101001394"}
      ]
    },
    "response_format": "json",
    "gscid": "[CLIENT_ID]",
    "gcid": "{{gcid}}",
    "request_type": "subscribe",
    "streaming_type": "ltpinfo"
  }
}
\`\`\`

**LTP Info Response Fields:**

| Field | Example | Description |
|-------|---------|-------------|
| \`name\` | \`"NESTLEIND"\` | Symbol name |
| \`symbol\` | \`"101017963"\` | Token |
| \`ltp\` | \`"18944.85"\` | Last traded price |
| \`change\` | \`"-1645.10"\` | Absolute change |
| \`p_change\` | \`"-7.99"\` | Percentage change |
| \`ltt\` | \`"14-10-2022 15:07:27"\` | Last trade time |
| \`lut\` | \`"14-10-2022 15:07:31"\` | Last update time |

> LTP is lightweight — only 6 fields vs ~30+ for marketPicture. Use for watchlists.

---

### Subscribe to Index Data

\`\`\`json
{
  "request": {
    "data": {
      "symbols": ["101999957", "101999972"]
    },
    "response_format": "json",
    "gscid": "[CLIENT_ID]",
    "gcid": "{{gcid}}",
    "request_type": "subscribe",
    "streaming_type": "index"
  }
}
\`\`\`

> **IMPORTANT**: Symbol format is INCONSISTENT between streaming types:
> - \`index\` uses plain string array: \`"symbols": ["101999972"]\`
> - \`ltpinfo\` and \`marketPicture\` use object array: \`"symbols": [{"symbol": "101017963"}]\`

**Index Response Fields:**

| Field | Example | Description |
|-------|---------|-------------|
| \`symbol\` | \`"Nifty Bank"\` | Index name |
| \`indexCode\` | \`"101999972"\` | Index token |
| \`ltp\` | \`"39263.50"\` | Last traded price |
| \`change\` | \`"639.50"\` | Absolute change |
| \`p_change\` | \`"1.66"\` | Percentage change |
| \`exch\` | \`"NSE"\` | Exchange |

**Known Index Tokens:**

| Token | Index |
|-------|-------|
| \`101999957\` | Nifty 50 |
| \`101999972\` | Nifty Bank |

---

### Unsubscribe from Market Data

Change \`request_type\` from \`"subscribe"\` to \`"unsubscribe"\` with same payload:

- Use \`streaming_type: "index"\` for indices
- Use \`streaming_type: "marketPicture"\` for scrips/tokens
- Use \`streaming_type: "ltpinfo"\` for LTP

---

### Complete WebSocket Streaming Types

| \`streaming_type\` | Direction | WebSocket | Purpose |
|-----------------|-----------|-----------|---------|
| \`login\` | → Server | Both | Authenticate WebSocket |
| \`LoginResponse\` | ← Server | Both | Login confirmation |
| \`HeartBeat\` | ↔ Both | Both | Keep-alive |
| \`index\` | → Apollo | Apollo | Subscribe to index data |
| \`ltpinfo\` | → Apollo | Apollo | Subscribe to LTP updates |
| \`marketPicture\` | → Apollo | Apollo | Subscribe to full market depth |
| \`NewOrderRequest\` | → IRIS | IRIS | Place new order |
| \`CancelOrderRequest\` | → IRIS | IRIS | Cancel order |
| \`ModifyOrderRequest\` | → IRIS | IRIS | Modify order |
| \`OrderResponse\` | ← IRIS | IRIS | Order status update |
| \`NPRequest\` | → IRIS | IRIS | Net position request |
| \`NPResponse\` | ← IRIS | IRIS | Net position response |
| \`NPDetailRequest\` | → IRIS | IRIS | Detailed net position |
| \`NPDetailResponse\` | ← IRIS | IRIS | Detailed NP response |
| \`HoldingValueInfo\` | → IRIS | IRIS | Holdings request |
| \`HoldingValueInfoResp\` | ← IRIS | IRIS | Holdings response |

---

## PART 6: Orders

### Place New Order (REST)

\`\`\`
POST http://vpn.greeksoft.in:8230/NewOrderRequest
Authorization: {{sessionToken}}
\`\`\`

**Request Body:**

\`\`\`json
{
  "request": {
    "data": {
      "trigger_price": "0",
      "gtoken": "101002885",
      "side": "1",
      "gcid": "{{gcid}}",
      "validity": "0",
      "price": "2500",
      "exchange": "NSE",
      "disclosed_qty": "0",
      "tradeSymbol": "RELIANCE",
      "lot": "1",
      "order_type": "1",
      "product": "0",
      "qty": "1",
      "corderid": "1",
      "amo": "0",
      "iprocli": "2",
      "gtdExpiry": 0,
      "is_post_closed": "0",
      "is_preopen_order": "0",
      "isSqOffOrder": "false",
      "offline": "0",
      "is_restapi": "1",
      "AccountNumber": "",
      "algoId": "",
      "strategyName": "STOCK",
      "settlor": ""
    },
    "response_format": "json",
    "request_type": "subscribe",
    "streaming_type": "NewOrderRequest"
  }
}
\`\`\`

**Mandatory Fields:**

| Field | Example | Validation Rule |
|-------|---------|----------------|
| \`gtoken\` | \`"102039287"\` | Must match \`tradeSymbol\` |
| \`side\` | \`"1"\` | 1=Buy, 2=Sell |
| \`gcid\` | \`"481"\` | Must be valid client ID |
| \`validity\` | \`"0"\` | 0=DAY, 1=IOC, 3=GTD |
| \`price\` | \`"25.00"\` | Zero price validation for Limit. Market/SLM: must be \`"0"\` not blank |
| \`exchange\` | \`"NSE"\` | NSE, BSE, MCX |
| \`disclosed_qty\` | \`"0"\` | — |
| \`tradeSymbol\` | \`"bankbaroda"\` | Must match gtoken. F&O: use \`NIFTY\`/\`BANKNIFTY\` |
| \`lot\` | \`"1"\` | Must be correct. For equity: \`lot = qty\` |
| \`order_type\` | \`"1"\` | 1=Limit, 2=Market, 3=SL, 4=SLM |
| \`product\` | \`"0"\` | 0=DELIVERY, 1=INTRADAY |
| \`qty\` | \`"2925"\` | — |
| \`is_post_closed\` | \`"0"\` | Market status dependent |
| \`is_preopen_order\` | \`"0"\` | Market status dependent |
| \`is_restapi\` | \`"1"\` | **Must be "1"** — invalid value causes rejection |
| \`iprocli\` | \`"0"\` | 0=Retailer, 1=Dealer-thru-Retailer, 2=Dealer |
| \`AccountNumber\` | \`""\` | Mandatory for dealer-through-retailer orders only (iprocli=1) |
| \`trigger_price\` | \`""\` | Mandatory for SL/SLM orders only |

**Non-Mandatory Fields:**

| Field | Default | When Mandatory |
|-------|---------|----------------|
| \`corderid\` | \`"3"\` | Never mandatory |
| \`amo\` | \`"0"\` | Set to \`"1"\` for After Market Orders |
| \`gtdExpiry\` | \`""\` | Mandatory when validity=3 (GTD) |
| \`isSqOffOrder\` | \`"false"\` | — |
| \`offline\` | \`"0"\` | Set to \`"1"\` for offline orders |
| \`algoId\` | \`""\` | Mandatory for algo orders only — requires exchange approval |
| \`strategyName\` | \`"STOCK"\` | Default: \`"GREEKSOFT"\` if blank/omitted |
| \`settlor\` | \`""\` | Mandatory for participant ID orders — validation pending |

**Order Type Price Rules:**

| Order Type | \`price\` | \`trigger_price\` |
|------------|---------|-----------------|
| Limit (1) | Required (non-zero) | Not needed |
| Market (2) | Must be \`"0"\` (not blank) | Not needed |
| SL (3) | Required | Required |
| SLM (4) | Must be \`"0"\` | Required |

**iprocli Values (Authoritative):**

| Value | Meaning | Notes |
|-------|---------|-------|
| \`"0"\` | Retailer | Default for retail end-user orders |
| \`"1"\` | Dealer through Retailer | Dealer placing on behalf of retailer — \`AccountNumber\` mandatory |
| \`"2"\` | Dealer | Dealer's own proprietary order (SVIS [CLIENT_ID] uses this) |

> For SVIS [CLIENT_ID]: Always use \`iprocli: "2"\` (PRO/Dealer)

**Order Response:**

Order response via REST returns order confirmation. If using WebSocket (IRIS), real-time status updates come as \`OrderResponse\` streaming type.

**Order Response Fields (via WebSocket):**

\`\`\`json
{
  "order_status": "Pending",
  "eorderid": "20220414000002",
  "lu_time_exchange": "1334401099",
  "lu_time": "1649914099",
  "regular_lot": "1",
  "order_state": "0",
  "cancelledBy": "",
  "goodTillDate": "0"
}
\`\`\`

**Order State Codes:**

| Code | State |
|------|-------|
| 0 | New |
| 1 | Modified |
| 2 | Cancelled |

---

### Place Order via WebSocket (IRIS)

\`\`\`
ws://vpn.greeksoft.in:8228
\`\`\`

Same JSON payload as REST. The minimum required fields for WebSocket order are:

\`gtoken\`, \`side\`, \`gcid\`, \`price\`, \`order_type\`, \`qty\`, \`iprocli\` (7 fields).

Server has defaults for omitted fields (exchange=NSE, product=0, validity=0).

---

### Cancel Order (REST — Simplified)

\`\`\`
DELETE http://vpn.greeksoft.in:8230/Order/{greekOrderId}
Authorization: {{sessionToken}}
\`\`\`

**Example:**

\`\`\`
DELETE http://vpn.greeksoft.in:8230/Order/120000104
\`\`\`

Only mandatory field: order number in URL path.

---

### Cancel Order via WebSocket (IRIS — Full)

For WebSocket cancellation, more fields are required (echoing from orderbook):

| Cancel Request Field | Source (from Orderbook) |
|---------------------|------------------------|
| \`gorderid\` | \`uniqueID\` |
| \`eorderid\` | \`uniqueOrderID\` |
| \`lu_time_exchange\` | \`ordModTime\` |
| \`pending_disclosed_qty\` | \`pendingdiscQty\` |
| \`pending_qty\` | \`pendingQty\` |
| \`qty_filled_today\` | \`trdQty\` |
| \`amo\` | \`otype == 6\` |
| \`offline\` | \`otype == 5\` |
| \`lexchangeOrderNo1\` | \`ordID\` |
| \`gtdExpiry\` | \`lgoodtilldate\` |
| \`iomRuleNo\` | \`lIOMRuleNo\` |
| \`iStrategyNo\` | \`iStrategyId\` |

---

### Modify Order (REST)

\`\`\`
POST http://vpn.greeksoft.in:8230/SmallModifyOrderRequest
Authorization: {{sessionToken}}
\`\`\`

**Request Body:**

\`\`\`json
{
    "request": {
        "data": {
            "trigger_price": "0",
            "gcid": "{{gcid}}",
            "validity": "0",
            "price": "2600",
            "disclosed_qty": "0",
            "order_type": "1",
            "lot": "1",
            "qty": "1",
            "gorderid": "120000003",
            "amo": "0",
            "gtdExpiry": 0
        },
        "response_format": "json",
        "request_type": "subscribe",
        "streaming_type": "ModifyOrderRequest"
    }
}
\`\`\`

**Field Classification:**

| Classification | Fields |
|---------------|--------|
| Mandatory | \`gcid\`, \`validity\`, \`price\`, \`disclosed_qty\`, \`order_type\`, \`qty\`, \`lot\`, \`gorderid\`, \`trigger_price\` (SL/SLM only) |
| Non-mandatory | \`amo\` (AMO only), \`gtdExpiry\` (GTD only), \`request_type\`, \`streaming_type\` |

---

## PART 7: Order Status

### Orderbook (with filters)

\`\`\`
GET http://vpn.greeksoft.in:8230/getOrderBookDetailWithLegV2?exchangeType=All&ClientCode={{gcid}}&Order_Status=ALL&Ordertype=All&gscid=[CLIENT_ID]
Authorization: {{sessionToken}}
\`\`\`

**Mandatory Parameters:** \`gscid\`, \`Ordertype\`, \`Order_Status\`, \`ClientCode\`, \`exchangeType\`

**Order Status Filter Values:**

| Value | Description |
|-------|-------------|
| \`ALL\` | All orders |
| \`Pending\` / \`PENDING\` / \`PENDING_BAJAJ\` | Pending orders |
| \`Traded\` / \`TRADED\` | Executed orders |
| \`RMS_REJECTED\` / \`RMS REJECTED\` | RMS rejected orders |
| \`CANCELLED\` | User cancelled orders |
| \`Unconfirmed\` | Sent but not acknowledged |
| \`Exchange Rejected\` | Rejected by exchange |

**Orderbook Response Fields per Order:**

\`\`\`
BookType, LegInfo[], LogTime, OrderFlags, action, amount, assetType,
cPANNumber, clientCode, dSLPrice, dSLTPrice, dTargetPrice, description,
discQty, errorCode, exchange, expiryDate, filterKey, flowType, iNoOfLegs,
iStrategyId, instrument, isCancellable, isEditable, lIOMRuleNo, lastModBy,
lgoodtilldate, lotSize, multiplier, optionType, ordID, ordLiveDays,
ordModTime, ordTime, orderType, otype, pendingQty, pendingdiscQty, price,
product, qty, remarks, scripName, status, strategyName, strikePrice, tag,
tickSize, tmplotSize, token, tradeSymbol, trdQty, trigPrice, uniqueID,
uniqueOrderID, userID, NoOfLegs
\`\`\`

---

### Single Order Detail

\`\`\`
GET http://vpn.greeksoft.in:8230/getOrderDetail?greekOrderNo=120000030&gscid=[CLIENT_ID]
Authorization: {{sessionToken}}
\`\`\`

**Mandatory Parameters:** \`greekOrderNo\`, \`gscid\`

---

## PART 8: Portfolio

### Net Position (Summary)

\`\`\`
POST http://vpn.greeksoft.in:8230/NPRequest
Authorization: {{sessionToken}}
\`\`\`

**Request Body:**

\`\`\`json
{
  "request": {
    "FormFactor": "M",
    "data": {
      "gscid": "[CLIENT_ID]"
    },
    "svcGroup": "portfolio",
    "svcVersion": "1.0.0",
    "streaming_type": "NPRequest",
    "request_type": "subscribe"
  }
}
\`\`\`

**Field Classification:**

| Classification | Fields | Notes |
|---------------|--------|-------|
| Mandatory | \`gscid\` | No rejection if blank |
| Mandatory | \`streaming_type\` | Required |
| Mandatory | \`request_type\` | Required |
| Non-mandatory | \`FormFactor\` | — |
| Non-mandatory | \`svcGroup\` | — |
| Non-mandatory | \`svcVersion\` | — |

**Response Fields per Position:**

\`NSEToken\`, \`BSEToken\`, \`token\`, \`netQty\`, \`DayNetAmt\`, \`preNetQty\`, \`PAmt\`, \`ProductType\`, \`symbol\`, \`isin\`, \`instrument\`, \`description\`, \`lotQty\`, \`sqoffToken\`, \`account\`, \`multiplier\`, \`price_multiplier\`

---

### Net Position (Detailed)

\`\`\`
POST http://vpn.greeksoft.in:8230/NPDetailRequest
Authorization: {{sessionToken}}
\`\`\`

Same request structure as NPRequest but with \`streaming_type: "NPDetailRequest"\`.

**Additional Response Fields:**

\`buyQty\`, \`buyAmt\`, \`sellQty\`, \`sellAmt\`, \`tickSize\`, \`expiry_date\`, \`option_type\`, \`strike_price\`, \`ltp\`, \`close\`

---

### Net Position MTM

\`\`\`
GET http://vpn.greeksoft.in:8230/getNetPositionMTM?gscid=[CLIENT_ID]
Authorization: {{sessionToken}}
\`\`\`

**Response:**

\`\`\`json
{
  "ErrorCode": 0,
  "data": [
    {"MTM": 794.79}
  ],
  "message": "",
  "success": "true"
}
\`\`\`

---

### Strategy-Wise Net Position

\`\`\`
GET http://vpn.greeksoft.in:8230/getStrategyNameWiseNetPositionDetail?gscid=[CLIENT_ID]
Authorization: {{sessionToken}}
\`\`\`

**Three Usage Patterns:**

| Pattern | URL | Returns |
|---------|-----|---------|
| Dealer + specific retailer | \`?gscid=[CLIENT_ID]&accountnumber=E015\` | Positions for specific retailer |
| Dealer + all retailers | \`?gscid=[CLIENT_ID]\` | All retailer positions for dealer |
| Retailer direct | \`?gscid=E015\` | All positions for retailer |

Provides strategy-wise P&L breakdown — separates positions by strategy name.

---

### Holdings

\`\`\`
POST http://vpn.greeksoft.in:8230/HoldingValueInfo
Authorization: {{sessionToken}}
\`\`\`

**Request Body:**

\`\`\`json
{
  "request": {
    "FormFactor": "M",
    "data": {
      "gscid": "[CLIENT_ID]",
      "gcid": "{{gcid}}",
      "sessionId": ""
    },
    "svcGroup": "portfolio",
    "svcVersion": "1.0.0",
    "streaming_type": "HoldingValueInfo",
    "request_type": "subscribe"
  }
}
\`\`\`

**Field Classification:**

| Classification | Fields |
|---------------|--------|
| Mandatory | \`streaming_type\`, \`request_type\` |
| Non-mandatory | \`gscid\`, \`gcid\`, \`sessionId\`, \`FormFactor\`, \`svcGroup\`, \`svcVersion\` |

**Response Fields:**

\`gscid\`, \`HValue\` (holding value), \`CValue\` (current value)

---

### Margin Details

\`\`\`
POST http://vpn.greeksoft.in:8230/MarginDetailRequest
Authorization: {{sessionToken}}
\`\`\`

**Request Body:**

\`\`\`json
{
  "request": {
    "FormFactor": "M",
    "data": {
      "gcid": "{{gcid}}",
      "sessionId": "",
      "segment": 3,
      "exchange_type": "-1"
    },
    "svcGroup": "portfolio",
    "svcVersion": "1.0.0",
    "streaming_type": "MarginDetailRequest",
    "request_type": "subscribe"
  }
}
\`\`\`

**Field Classification:**

| Classification | Fields |
|---------------|--------|
| Mandatory | \`gcid\`, \`segment\`, \`exchange_type\` |
| Non-mandatory | \`FormFactor\`, \`sessionId\`, \`svcGroup\`, \`svcVersion\` |

---

## PART 9: Watchlists

### Get Watchlist Groups

\`\`\`
POST http://vpn.greeksoft.in:8230/getWatchlistGroupsNew_MobileV2
Authorization: {{sessionToken}}
\`\`\`

**Request Body:**

\`\`\`json
{
  "request": {
    "data": {
      "gscid": "[CLIENT_ID]"
    },
    "svcName": "getWatchlistGroupsNew_MobileV2",
    "svcGroup": "Portfolio"
  }
}
\`\`\`

Returns all watchlist groups with symbol lists. Each symbol includes \`token\`, \`exchange\`, \`assetType\`, \`tradeSymbol\`, \`ltp\`, \`close\`, \`change\`, \`p_change\`.

---

### Get Watchlist by Group Name

\`\`\`
POST http://vpn.greeksoft.in:8230/getWatchlistDataByGroupName_MobileV2
Authorization: {{sessionToken}}
\`\`\`

**Request Body:**

\`\`\`json
{
  "request": {
    "data": {
      "gscid": "[CLIENT_ID]",
      "WatchListType": "USER",
      "WatchListGroup": "WatchList1"
    },
    "svcName": "getWatchlistDataByGroupName_MobileV2",
    "svcGroup": "Portfolio"
  }
}
\`\`\`

---

## PART 10: Complete Field Reference

### Order Fields

| Field | Values | Description |
|-------|--------|-------------|
| \`side\` | \`"1"\` = Buy, \`"2"\` = Sell | Order side |
| \`order_type\` | \`"1"\` = Limit, \`"2"\` = Market, \`"3"\` = SL, \`"4"\` = SLM | Order type |
| \`product\` | \`"0"\` = DELIVERY, \`"1"\` = INTRADAY | Product type |
| \`iprocli\` | \`"0"\` = Retailer, \`"1"\` = Dealer-thru-Retailer, \`"2"\` = Dealer | Order origin |
| \`validity\` | \`"0"\` = DAY, \`"1"\` = IOC, \`"3"\` = GTD | Validity |
| \`exchange\` | \`"NSE"\`, \`"BSE"\`, \`"MCX"\` | Exchange |
| \`is_restapi\` | \`"1"\` | Mandatory — invalid value = rejection |
| \`amo\` | \`"0"\` = Normal, \`"1"\` = After Market | AMO flag |
| \`offline\` | \`"0"\` = Normal, \`"1"\` = Offline | Offline flag |
| \`gtoken\` | e.g. \`"101014947"\` | Greeksoft instrument token |
| \`gcid\` | e.g. \`"82"\` | Global Client ID |
| \`tradeSymbol\` | e.g. \`"NIFTY"\`, \`"WIPRO"\` | Trading symbol |
| \`qty\` | e.g. \`"25"\` | Total quantity |
| \`lot\` | e.g. \`"1"\` | Number of lots (for equity: lot = qty) |
| \`price\` | e.g. \`"380"\` | Order price |
| \`trigger_price\` | e.g. \`"0"\` | SL trigger price |
| \`disclosed_qty\` | e.g. \`"0"\` | Disclosed quantity |
| \`algoId\` | e.g. \`"555200"\` | Algorithm ID (algo orders — needs exchange approval) |
| \`strategyName\` | e.g. \`"Straddle"\`, \`"STOCK"\` | Strategy tag (default: "GREEKSOFT") |
| \`AccountNumber\` | e.g. \`"NIKR2"\` | Client account code (CLI orders) |
| \`gorderid\` | e.g. \`"120000003"\` | Greek order ID (for modify/cancel) |
| \`corderid\` | e.g. \`"3"\` | Client order ID |
| \`settlor\` | \`""\` | Participant ID — validation pending |

### Identity Fields

| Field | Description |
|-------|-------------|
| \`gscid\` | \`"[CLIENT_ID]"\` — Client ID (must be UPPERCASE) |
| \`gcid\` | Dynamic — from getLoginInfo response (changes per session) |
| \`sessionId\` | From jloginNew response (new each login) |
| \`sessionToken\` | JWT RS256 from auth endpoint |

### Token Format

- Greeksoft tokens (\`gtoken\`): 9-digit numeric strings
- Format: \`1XXYYYYYY\` where \`XX\` = segment code, \`YYYYYY\` = scrip ID
- Examples: \`101014947\` (NSE equity), \`102045459\` (NSE option), \`102046343\` (NSE F&O)
- Index tokens follow \`101999XXX\` pattern: \`101999957\` = Nifty 50, \`101999972\` = Nifty Bank

---

## PART 11: Operational Rules

### Message Rate

- Default = 5 messages/second per user
- Must request increase from Greeksoft for algo trading
- JWT payload shows \`message_rate\` field

### Session Token Lifecycle

- Only ONE active session token per user
- Generating new token invalidates previous
- Default expiry = 4 hours, configurable via \`validFor\`
- 401 response when expired/invalid

### GCID Dynamics

- Not constant — changes between sessions
- Must fetch fresh via getLoginInfo each session
- Cannot hardcode or cache across sessions

### SessionID

- Generated fresh every jloginNew call
- Previous becomes invalid
- Used for WebSocket auth and heartbeats

### WebSocket Timeout

- 15-minute inactivity timeout
- Must send heartbeats regularly to IRIS and Apollo
- On disconnect: re-login required

### Heartbeat Rules

- Interval from getFlagValues (default 10 seconds)
- 3 failed heartbeats → re-login required
- Error code other than 0 in heartbeat → logged out
- Three heartbeats run simultaneously: REST (jheartbeat to Arachne), WS (to IRIS), WS (to Apollo)

### Base64 Encoding

- Server-side toggle in CTCLManager.ini: \`isBASE64=true\`
- When true: GET parameters must be Base64 encoded
- When false: plain text accepted
- \`code\` parameter in scrip lookups is always Base64

### Validation Rules

- All prices and quantities are **strings**, not numbers
- \`tradeSymbol\` + \`gtoken\` must match — mismatch causes rejection
- \`lot = qty\` for equity orders
- \`gscid\` must be in CAPITAL letters
- \`is_restapi\` invalid value causes rejection
- \`strategyName\` defaults to "GREEKSOFT" if blank
- Market/SLM price must be \`"0"\` not blank
- \`svcName\`/\`svcGroup\` work even if blank/invalid for most endpoints

---

## PART 12: Server Configuration (CTCLManager.ini)

\`\`\`ini
[DBSettings]
Config\\isEnableIris=true

[IBT]
Enable_Iris=true
EnableRestcpp=true
Rest_API_port=3333             # Default (SVIS uses 8230)
Rest_webApolloSocket_port=8082 # Default (SVIS uses 8229)
Rest_webSocket_port=8085       # Default (SVIS uses 8228)
ValidateSessionFromDB=true
app_version_no=1.0.1.10
company_name=Your_Broker_Name
isBASE64=true
isSecure=false
\`\`\`

**Broadcast Exchange IP/Port Settings (dummy/default):**

| Exchange | Segment | IP | Port |
|----------|---------|-----|------|
| NSE | Cash | 192.168.207.92 | 9981 |
| NFO | F&O | 192.168.207.92 | 9996 |
| BSE | Cash | 192.168.204.17 | 9987 |
| BCD | Currency | 192.168.204.17 | 9981 |
| BFO | BSE F&O | 192.168.204.17 | 9984 |

**Preferred Indices:**

- preferredIndex1 = Nifty_50
- preferredIndex2 = Nifty_Bank
- preferredIndex3 = Sensex

---

## PART 13: Admin Setup & Support

### Admin Panel Requirements

1. Enable IBT (Internet-Based Trading) for the user
2. Set secondary password to: \`[REDACTED]\`
3. In 'Market Allowed' section: Enable "Automated" option
4. In Admin > User > Arbitrage Mapper: Map the RESTAPI product to user

### Support

**Email:** \`supportteam@greeksoft.co.in\`

### API Product Lines

- Order Management API
- Order Placement API
- Risk Management API
- Admin API
- Strategy Finder & Strategy Builder API
- Option Chain API
- Margin Calculation API

### Python Library

\`\`\`bash
pip install greekapi
\`\`\`

\`\`\`python
from greeksoft import GreekAPI

api = GreekAPI(
    username="[CLIENT_ID]",
    session_pwd="[REDACTED]",
    user_pwd="<CTCL_PASSWORD>",
    procli="2",
    ac_no=""
)
\`\`\`

**PyPI:** https://pypi.org/project/greeksoft/

---

## PART 14: Complete Endpoint Inventory

### Authentication & Login

| # | Endpoint | Method | URL |
|---|----------|--------|-----|
| 1 | Session Token | POST | \`http://greekapi.greeksoft.in:3001/auth/greek/sessiontoken\` |
| 2 | Get Flag Values | POST | \`http://vpn.greeksoft.in:8230/getFlagValues\` |
| 3 | Login | POST | \`http://vpn.greeksoft.in:8230/jloginNew\` |
| 4 | Validate MPIN | POST | \`http://vpn.greeksoft.in:8230/validateMPIN\` |
| 5 | Get Login Info | POST | \`http://vpn.greeksoft.in:8230/getLoginInfo\` |
| 6 | REST Heartbeat | POST | \`http://vpn.greeksoft.in:8230/jheartbeat\` |
| 7 | Get Allowed Products | POST | \`http://vpn.greeksoft.in:8230/getAllowedProduct\` |

### Instruments & Contracts

| # | Endpoint | Method | URL |
|---|----------|--------|-----|
| 8 | Get All Contracts | GET | \`http://vpn.greeksoft.in:8230/getAllContract\` |
| 9 | Scrip Details | GET | \`http://vpn.greeksoft.in:8230/getFullScripDetailsBySymbol_Mobile\` |
| 10 | Option Chain | GET | \`http://vpn.greeksoft.in:8230/getFullScripDetailsBySymbol_Option\` |
| 11 | Expiry Dates | GET | \`http://vpn.greeksoft.in:8230/getFullScripDetailsBySymbol_Expiry\` |

### Market Data (REST)

| # | Endpoint | Method | URL |
|---|----------|--------|-----|
| 12 | Market Status | POST | \`http://vpn.greeksoft.in:8230/getMarketStatus\` |
| 13 | Single Symbol Quote | POST | \`http://vpn.greeksoft.in:8230/getQuoteForSingleSymbol_V2\` |
| 14 | Multiple Symbol Quotes | POST | \`http://vpn.greeksoft.in:8230/getQuoteForMultipleSymbols\` |
| 15 | Indian Indices | GET | \`http://vpn.greeksoft.in:8230/getIndianIndicesData_V2\` |
| 16 | Market-by-Price | GET | \`http://vpn.greeksoft.in:8230/getToken_OnlyMbpData\` |
| 17 | OHLC Chart Data | POST | \`http://vpn.greeksoft.in:8230/get_ohlc\` |

### Market Data (WebSocket — Apollo)

| # | Endpoint | Protocol | URL |
|---|----------|----------|-----|
| 18 | Apollo Login | WS | \`ws://vpn.greeksoft.in:8229\` |
| 19 | Apollo Heartbeat | WS | \`ws://vpn.greeksoft.in:8229\` |
| 20 | Market Picture Subscribe | WS | \`ws://vpn.greeksoft.in:8229\` |
| 21 | LTP Info Subscribe | WS | \`ws://vpn.greeksoft.in:8229\` |
| 22 | Index Subscribe | WS | \`ws://vpn.greeksoft.in:8229\` |
| 23 | Unsubscribe | WS | \`ws://vpn.greeksoft.in:8229\` |

### Orders

| # | Endpoint | Method | URL |
|---|----------|--------|-----|
| 24 | Place Order (REST) | POST | \`http://vpn.greeksoft.in:8230/NewOrderRequest\` |
| 25 | Cancel Order (REST) | DELETE | \`http://vpn.greeksoft.in:8230/Order/{id}\` |
| 26 | Modify Order (REST) | POST | \`http://vpn.greeksoft.in:8230/SmallModifyOrderRequest\` |
| 27 | Place Order (WS) | WS | \`ws://vpn.greeksoft.in:8228\` |
| 28 | Cancel Order (WS) | WS | \`ws://vpn.greeksoft.in:8228\` |
| 29 | Modify Order (WS) | WS | \`ws://vpn.greeksoft.in:8228\` |
| 30 | IRIS Login | WS | \`ws://vpn.greeksoft.in:8228\` |
| 31 | IRIS Heartbeat | WS | \`ws://vpn.greeksoft.in:8228\` |

### Order Status

| # | Endpoint | Method | URL |
|---|----------|--------|-----|
| 32 | Orderbook | GET | \`http://vpn.greeksoft.in:8230/getOrderBookDetailWithLegV2\` |
| 33 | Single Order Detail | GET | \`http://vpn.greeksoft.in:8230/getOrderDetail\` |

### Portfolio

| # | Endpoint | Method | URL |
|---|----------|--------|-----|
| 34 | Net Position (Summary) | POST | \`http://vpn.greeksoft.in:8230/NPRequest\` |
| 35 | Net Position (Detailed) | POST | \`http://vpn.greeksoft.in:8230/NPDetailRequest\` |
| 36 | Net Position MTM | GET | \`http://vpn.greeksoft.in:8230/getNetPositionMTM\` |
| 37 | Strategy-Wise Position | GET | \`http://vpn.greeksoft.in:8230/getStrategyNameWiseNetPositionDetail\` |
| 38 | Holdings | POST | \`http://vpn.greeksoft.in:8230/HoldingValueInfo\` |
| 39 | Margin Details | POST | \`http://vpn.greeksoft.in:8230/MarginDetailRequest\` |

### Watchlists

| # | Endpoint | Method | URL |
|---|----------|--------|-----|
| 40 | Watchlist Groups | POST | \`http://vpn.greeksoft.in:8230/getWatchlistGroupsNew_MobileV2\` |
| 41 | Watchlist by Group | POST | \`http://vpn.greeksoft.in:8230/getWatchlistDataByGroupName_MobileV2\` |

---

## PART 15: Architecture Diagram

\`\`\`mermaid
graph LR
    subgraph "Aalap"
        A[Backend Server]
    end
    subgraph "Greeksoft Auth"
        B["greekapi.greeksoft.in:3001"]
    end
    subgraph "SVIS Broker (vpn.greeksoft.in)"
        C["Arachne REST :8230"]
        D["IRIS WS :8228"]
        E["Apollo WS :8229"]
    end
    A -->|"1. Session Token"| B
    B -->|"JWT RS256"| A
    A -->|"2-8. REST API calls"| C
    C -->|"JSON responses"| A
    A -->|"Orders & Responses"| D
    D -->|"Order status callbacks"| A
    A -->|"Live Market Data"| E
    E -->|"Quotes, Depth, Indices"| A
\`\`\`

---

## PART 16: Environment Configuration

\`\`\`env
# Greeksoft SVIS Broker Configuration
GREEK_CLIENT_ID=[CLIENT_ID]
GREEK_SESSION_PASSWORD=[REDACTED]
GREEK_CTCL_PASSWORD=           # PENDING - obtain from SVIS broker
GREEK_USER_TYPE=PRO
GREEK_IPROCLI=2
GREEK_BROKER_ID=1
GREEK_VERSION_NO=1.0.1.10

# URLs
GREEK_SESSION_TOKEN_URL=http://greekapi.greeksoft.in:3001/auth/greek/sessiontoken
GREEK_REST_BASE_URL=http://vpn.greeksoft.in:8230
GREEK_WS_IRIS_URL=ws://vpn.greeksoft.in:8228
GREEK_WS_APOLLO_URL=ws://vpn.greeksoft.in:8229

# Operational
GREEK_HEARTBEAT_INTERVAL=10
GREEK_SESSION_VALIDITY=1d
GREEK_MESSAGE_RATE=5
\`\`\`

**Support Email:** \`supportteam@greeksoft.co.in\`

---

## Appendix A: Postman Collection Reference

### Collection Metadata

\`\`\`json
{
    "info": {
        "_postman_id": "6de94515-ef70-40d0-bfe0-f0ec91b2d16c",
        "name": "Greek RESTAPI",
        "schema": "https://schema.getpostman.com/json/collection/v2.1.0/collection.json"
    }
}
\`\`\`

> All URLs below use the SVIS broker-specific configuration. \`gscid\` is set to \`[CLIENT_ID]\` throughout.

---

### Folder 1: Session Token

#### POST — Get Session Token

\`\`\`
POST http://greekapi.greeksoft.in:3001/auth/greek/sessiontoken
\`\`\`

**Request Body:**

\`\`\`json
{
    "username": "[CLIENT_ID]",
    "password": "[REDACTED]",
    "validFor": "1d"
}
\`\`\`

**Test Script (Postman):**

\`\`\`javascript
pm.test("Status code is 201", function () {
    pm.response.to.have.status(201);
});

pm.test("Response time is less than 300ms", function () {
    pm.expect(pm.response.responseTime).to.be.below(300);
});

var jsonData = pm.response.json();
pm.environment.set("sessionToken", jsonData.sessionToken);
\`\`\`

---

### Folder 2: Login

#### 2.1 POST — jloginNew

\`\`\`
POST http://vpn.greeksoft.in:8230/jloginNew
Authorization: {{sessionToken}}
\`\`\`

**Pre-Request Script (Postman):**

\`\`\`javascript
// MD5 hash passwords ending in .md5
var password = pm.environment.get("ctcl_password");
if (password && password.endsWith(".md5")) {
    var CryptoJS = require('crypto-js');
    var hashed = CryptoJS.MD5(password.replace(".md5", "")).toString();
    pm.environment.set("ctcl_password_hashed", hashed);
} else {
    pm.environment.set("ctcl_password_hashed", password);
}
\`\`\`

**Request Body:**

\`\`\`json
{
  "request": {
    "data": {
      "pan_dob": "01/01/1901",
      "deviceId": "",
      "gscid": "[CLIENT_ID]",
      "deviceDetails": "",
      "deviceType": "0",
      "pass": "{{ctcl_password_hashed}}",
      "transPass": "",
      "userType": "Customer",
      "brokerid": "1",
      "passType": "0",
      "version_no": "1.0.1.10",
      "encryptionType": "1"
    },
    "svcName": "jloginNew",
    "svcGroup": "Login"
  }
}
\`\`\`

#### 2.2 POST — getLoginInfo

\`\`\`
POST http://vpn.greeksoft.in:8230/getLoginInfo
Authorization: {{sessionToken}}
\`\`\`

**Request Body:**

\`\`\`json
{
    "request": {
        "svcVersion": "1.0.0",
        "svcGroup": "Login",
        "svcName": "getLoginInfo",
        "assetType": "",
        "data": {
            "gscid": "[CLIENT_ID]"
        }
    }
}
\`\`\`

#### 2.3 POST — getFlagValues

\`\`\`
POST http://vpn.greeksoft.in:8230/getFlagValues
Authorization: {{sessionToken}}
\`\`\`

**Request Body:**

\`\`\`json
{
    "request": {
        "svcVersion": "1.0.0",
        "svcGroup": "",
        "svcName": "getFlagValues",
        "gscid": "",
        "assetType": "",
        "data": {}
    }
}
\`\`\`

#### 2.4 POST — getAllowedProduct

\`\`\`
POST http://vpn.greeksoft.in:8230/getAllowedProduct
Authorization: {{sessionToken}}
\`\`\`

**Request Body:**

\`\`\`json
{
    "request": {
        "data": {},
        "svcName": "getAllowedProduct",
        "svcGroup": "Login"
    }
}
\`\`\`

#### 2.5 POST — getMarketStatus

\`\`\`
POST http://vpn.greeksoft.in:8230/getMarketStatus
Authorization: {{sessionToken}}
\`\`\`

**Request Body:**

\`\`\`json
{
    "request": {
        "data": {
            "gscid": "[CLIENT_ID]"
        },
        "svcName": "getMarketStatus",
        "svcGroup": "Markets"
    }
}
\`\`\`

---

### Folder 3: Broadcast

#### 3.1 GET — getFullScripDetailsBySymbol_Mobile

\`\`\`
GET http://vpn.greeksoft.in:8230/getFullScripDetailsBySymbol_Mobile?exchange=NSE&assetType=equity&code=UkVMSUFOQ0U=&type=equity
Authorization: {{sessionToken}}
\`\`\`

**Query Parameters:**

| Param | Example Value | Description |
|-------|--------------|-------------|
| \`exchange\` | \`NSE\` | Exchange (NSE, BSE) |
| \`assetType\` | \`equity\` | Asset type (equity, future, option, currency, commodity) |
| \`code\` | \`UkVMSUFOQ0U=\` | Symbol name Base64 encoded (RELIANCE) |
| \`type\` | \`equity\` | Instrument type (equity, future, calloption, putoption) |

#### 3.2 POST — getQuoteForSingleSymbol_V2

\`\`\`
POST http://vpn.greeksoft.in:8230/getQuoteForSingleSymbol_V2
Authorization: {{sessionToken}}
\`\`\`

**Request Body:**

\`\`\`json
{
    "request": {
        "data": {
            "token": "101002885",
            "assetType": "equity",
            "gscid": "[CLIENT_ID]",
            "gcid": "{{gcid}}"
        },
        "svcName": "getQuoteForSingleSymbol_V2",
        "svcGroup": "Markets"
    }
}
\`\`\`

#### 3.3 GET — getIndianIndicesData_V2

\`\`\`
GET http://vpn.greeksoft.in:8230/getIndianIndicesData_V2
Authorization: {{sessionToken}}
\`\`\`

#### 3.4 GET — getToken_OnlyMbpData

\`\`\`
GET http://vpn.greeksoft.in:8230/getToken_OnlyMbpData
Authorization: {{sessionToken}}
\`\`\`

**Request Body:**

\`\`\`json
{
    "request": {
        "data": {
            "mode": "FULL",
            "exchangeTokens": [
                {
                    "exchange": "NSE",
                    "token": "101001594"
                },
                {
                    "exchange": "NSE",
                    "token": "102059456"
                }
            ]
        },
        "svcName": "getToken_OnlyMbpData",
        "svcGroup": "Markets"
    }
}
\`\`\`

---

### Folder 4: Trade Orders

#### 4.1 POST — NewOrderRequest (PRO Manual)

\`\`\`
POST http://vpn.greeksoft.in:8230/NewOrderRequest
Authorization: {{sessionToken}}
\`\`\`

**Request Body:**

\`\`\`json
{
  "request": {
    "data": {
      "trigger_price": "0",
      "gtoken": "101002885",
      "side": "1",
      "gcid": "{{gcid}}",
      "validity": "0",
      "price": "2500",
      "exchange": "NSE",
      "disclosed_qty": "0",
      "tradeSymbol": "RELIANCE",
      "lot": "1",
      "order_type": "1",
      "product": "0",
      "qty": "1",
      "corderid": "1",
      "amo": "0",
      "iprocli": "2",
      "gtdExpiry": 0,
      "is_post_closed": "0",
      "is_preopen_order": "0",
      "isSqOffOrder": "false",
      "offline": "0",
      "is_restapi": "1",
      "AccountNumber": "",
      "algoId": "",
      "strategyName": "STOCK",
      "settlor": ""
    },
    "response_format": "json",
    "request_type": "subscribe",
    "streaming_type": "NewOrderRequest"
  }
}
\`\`\`

#### 4.2 POST — NewOrderRequest (PRO Algo)

\`\`\`
POST http://vpn.greeksoft.in:8230/NewOrderRequest
Authorization: {{sessionToken}}
\`\`\`

**Request Body:**

\`\`\`json
{
  "request": {
    "data": {
      "trigger_price": "0",
      "gtoken": "101002885",
      "side": "1",
      "gcid": "{{gcid}}",
      "validity": "0",
      "price": "2500",
      "exchange": "NSE",
      "disclosed_qty": "0",
      "tradeSymbol": "RELIANCE",
      "lot": "1",
      "order_type": "1",
      "product": "0",
      "qty": "1",
      "corderid": "1",
      "amo": "0",
      "iprocli": "2",
      "gtdExpiry": 0,
      "is_post_closed": "0",
      "is_preopen_order": "0",
      "isSqOffOrder": "false",
      "offline": "0",
      "is_restapi": "1",
      "AccountNumber": "",
      "algoId": "555200",
      "strategyName": "STOCK",
      "settlor": ""
    },
    "response_format": "json",
    "request_type": "subscribe",
    "streaming_type": "NewOrderRequest"
  }
}
\`\`\`

> **Note:** \`algoId: "555200"\` — Algo orders require prior exchange approval.

#### 4.3 POST — NewOrderRequest (CLI — Dealer through Retailer)

\`\`\`
POST http://vpn.greeksoft.in:8230/NewOrderRequest
Authorization: {{sessionToken}}
\`\`\`

**Request Body:**

\`\`\`json
{
  "request": {
    "data": {
      "trigger_price": "0",
      "gtoken": "101002885",
      "side": "1",
      "gcid": "{{gcid}}",
      "validity": "0",
      "price": "2500",
      "exchange": "NSE",
      "disclosed_qty": "0",
      "tradeSymbol": "RELIANCE",
      "lot": "1",
      "order_type": "1",
      "product": "0",
      "qty": "1",
      "corderid": "1",
      "amo": "0",
      "iprocli": "1",
      "gtdExpiry": 0,
      "is_post_closed": "0",
      "is_preopen_order": "0",
      "isSqOffOrder": "false",
      "offline": "0",
      "is_restapi": "1",
      "AccountNumber": "NIKR2",
      "algoId": "",
      "strategyName": "STOCK",
      "settlor": ""
    },
    "response_format": "json",
    "request_type": "subscribe",
    "streaming_type": "NewOrderRequest"
  }
}
\`\`\`

> **Note:** \`iprocli: "1"\` (Dealer through Retailer) — \`AccountNumber\` is **mandatory** and must contain the retailer's account code.

#### 4.4 POST — NewOrderRequest (PRO-to-CLI)

\`\`\`
POST http://vpn.greeksoft.in:8230/NewOrderRequest
Authorization: {{sessionToken}}
\`\`\`

**Request Body:**

\`\`\`json
{
  "request": {
    "data": {
      "trigger_price": "0",
      "gtoken": "101002885",
      "side": "1",
      "gcid": "{{gcid}}",
      "validity": "0",
      "price": "2500",
      "exchange": "NSE",
      "disclosed_qty": "0",
      "tradeSymbol": "RELIANCE",
      "lot": "1",
      "order_type": "1",
      "product": "0",
      "qty": "1",
      "corderid": "1",
      "amo": "0",
      "iprocli": "2",
      "gtdExpiry": 0,
      "is_post_closed": "0",
      "is_preopen_order": "0",
      "isSqOffOrder": "false",
      "offline": "0",
      "is_restapi": "1",
      "AccountNumber": "",
      "algoId": "",
      "strategyName": "STOCK",
      "settlor": ""
    },
    "response_format": "json",
    "request_type": "subscribe",
    "streaming_type": "NewOrderRequest"
  }
}
\`\`\`

#### 4.5 DELETE — Cancel Order

\`\`\`
DELETE http://vpn.greeksoft.in:8230/Order/120000104
Authorization: {{sessionToken}}
\`\`\`

> Replace \`120000104\` with the actual Greek order ID to cancel.

#### 4.6 POST — SmallModifyOrderRequest

\`\`\`
POST http://vpn.greeksoft.in:8230/SmallModifyOrderRequest
Authorization: {{sessionToken}}
\`\`\`

**Request Body:**

\`\`\`json
{
    "request": {
        "data": {
            "trigger_price": "0",
            "gcid": "{{gcid}}",
            "validity": "0",
            "price": "2600",
            "disclosed_qty": "0",
            "order_type": "1",
            "lot": "1",
            "qty": "1",
            "gorderid": "120000003",
            "amo": "0",
            "gtdExpiry": 0
        },
        "response_format": "json",
        "request_type": "subscribe",
        "streaming_type": "ModifyOrderRequest"
    }
}
\`\`\`

---

### Folder 5: Orderbook

#### 5.1 GET — getOrderBookDetailWithLegV2

\`\`\`
GET http://vpn.greeksoft.in:8230/getOrderBookDetailWithLegV2?exchangeType=All&ClientCode={{gcid}}&Order_Status=ALL&Ordertype=All&gscid=[CLIENT_ID]
Authorization: {{sessionToken}}
\`\`\`

**Query Parameters:**

| Param | Example Value | Description |
|-------|--------------|-------------|
| \`exchangeType\` | \`All\` | Exchange filter |
| \`ClientCode\` | \`{{gcid}}\` | Dynamic client code from getLoginInfo |
| \`Order_Status\` | \`ALL\` | Status filter (ALL, Pending, Traded, CANCELLED, etc.) |
| \`Ordertype\` | \`All\` | Order type filter |
| \`gscid\` | \`[CLIENT_ID]\` | Client ID |

#### 5.2 GET — getOrderDetail

\`\`\`
GET http://vpn.greeksoft.in:8230/getOrderDetail?greekOrderNo=120000030&gscid=[CLIENT_ID]
Authorization: {{sessionToken}}
\`\`\`

**Query Parameters:**

| Param | Example Value | Description |
|-------|--------------|-------------|
| \`greekOrderNo\` | \`120000030\` | Greek order number |
| \`gscid\` | \`[CLIENT_ID]\` | Client ID |

---

### Postman Environment Variables

| Variable | Description | Example |
|----------|-------------|---------|
| \`sessionToken\` | JWT from session token endpoint | \`eyJhbGciOiJSUzI1NiIs...\` |
| \`gcid\` | Dynamic client ID from getLoginInfo | \`481\` |
| \`ctcl_password\` | CTCL password (raw or .md5 suffix) | \`<PENDING>\` |
| \`ctcl_password_hashed\` | MD5-hashed CTCL password | Auto-generated by pre-request script |

---

## Appendix B: Python Library (greekapi) Code Samples

### Installation

\`\`\`bash
pip install greekapi
\`\`\`

**PyPI:** https://pypi.org/project/greeksoft/

### Basic Authentication & Setup

\`\`\`python
from greeksoft import GreekAPI

# User credentials
username = "[CLIENT_ID]"
session_pwd = "[REDACTED]"
user_pwd = "<CTCL_PASSWORD>"  # PENDING - obtain from SVIS broker
procli = "2"                  # 2 for dealer (PRO)
ac_no = ""                    # For retail ID, pass account number

# Authenticate with provided credentials
api = GreekAPI(username, session_pwd, user_pwd, procli, ac_no)
\`\`\`

### Parameter Reference

| Parameter | Value | Description |
|-----------|-------|-------------|
| \`username\` | \`"[CLIENT_ID]"\` | Greeksoft client ID (must be UPPERCASE) |
| \`session_pwd\` | \`"[REDACTED]"\` | Session password (for session token generation) |
| \`user_pwd\` | \`"<CTCL_PASSWORD>"\` | CTCL trading password (PENDING — obtain from SVIS broker) |
| \`procli\` | \`"2"\` | Order origin: \`"0"\` = Retailer, \`"1"\` = Dealer-thru-Retailer, \`"2"\` = Dealer (PRO) |
| \`ac_no\` | \`""\` | Account number — leave blank for PRO/Dealer. For dealer-through-retailer (\`procli="1"\`), pass the retailer's account number |

### Notes

- The \`greekapi\` Python library wraps the REST API calls described in this document.
- It handles session token management, MD5 password hashing, and heartbeat management internally.
- For SVIS [CLIENT_ID] (PRO/Dealer): always use \`procli="2"\` and leave \`ac_no=""\`.
- CTCL password is currently **PENDING** — obtain from SVIS broker before the library can authenticate successfully.

---

# PART 3: Aalap Integration History & Technical Implementation

## 1. Overview of the Migration & SVIS Port Configuration
- **Server Migration:** The trading server was successfully migrated from Oracle Cloud VPS to a new Hostinger VPS at IP \`80.225.242.244\`.
- **IP Registration / Access:** SVIS enforces server-side IP restrictions for socket connections. When migrating servers, SVIS broker administrators must register/whitelist the new server IP (\`80.225.242.244\`) to permit API calls and WebSocket streaming.
- **Port Mapping:**
  | Service | Host | Port | Protocol | Usage | Status |
  |---|---|---|---|---|---|
  | Auth API | \`greekapi.greeksoft.in\` | \`3001\` | HTTPS | Get Session JWT Token | Active |
  | REST API | \`vpn.greeksoft.in\` | \`8230\` | HTTPS | getSessionId, jloginNew, Positions, Orders | Active |
  | IRIS WebSocket | \`vpn.greeksoft.in\` | \`8228\` | WS (TCP) | Real-time Order & Executions push | Connected |
  | Apollo WebSocket | \`vpn.greeksoft.in\` | \`8229\` | WS (TCP) | Real-time Market Data (LTP) stream | Pending Credentials |

---

## 2. The Dual-Session Connection Flow
Greeksoft's SVIS WebSocket server has a strict constraint: **it will automatically close one connection if the same \`sessionId\` is used on multiple WebSockets simultaneously.**
To solve this, the Aalap trading engine performs a dual-session fetch sequence:
1. Call \`getSessionId\` for the **IRIS (Order) WebSocket** connection. This gives us \`irisSessionId\`.
2. Call \`jloginNew\` (or a second \`getSessionId\`) for the **Apollo (Market Data) WebSocket** connection. This gives us \`apolloSessionId\`.
3. Start the IRIS WS using \`irisSessionId\`, and start the Apollo WS using \`apolloSessionId\`.
4. Run independent heartbeat intervals for each session using their respective \`sessionId\`s.

---

## 3. IRIS WebSocket Integration (Order Updates)
The IRIS socket streams real-time updates for orders placed, modified, or filled.
- **WebSocket Endpoint:** \`ws://vpn.greeksoft.in:8228\`
- **Session Authentication:** Works directly with the session obtained from \`getSessionId\`.
- **Raw Login Frame Format:**
  Must be sent as raw JSON directly upon connection (do **not** wrap it in a \`request\` or \`data\` block):
  \`\`\`json
  {
    "gscid": "GREEK13",
    "gcid": "139",
    "sessionId": "$2a$10$4BvCsqFHTy9oYOBMIQwKce",
    "device_type": "0",
    "isRestApi": "1"
  }
  \`\`\`
- **Heartbeat Payload:**
  Must be sent every 10 seconds to keep the socket alive:
  \`\`\`json
  {
    "streaming_type": "HeartBeat",
    "gcid": "139",
    "sessionId": "$2a$10$4BvCsqFHTy9oYOBMIQwKce"
  }
  \`\`\`

---

## 4. Apollo WebSocket Integration (Market Data Feed)
The Apollo socket streams live market feeds (ticks, LTP, index prices).
- **WebSocket Endpoint:** \`ws://vpn.greeksoft.in:8229\`
- **Session Authentication:** Requires a session authenticated through \`jloginNew\` to grant market data rights.
- **Why \`getSessionId\` fails:** If an Apollo socket is connected using a session from \`getSessionId\`, SVIS accepts the login frame, but **closes the connection with code 1000** as soon as any symbol subscription frame is sent because that session lacks market data stream authorization.
- **Raw Login Frame Format:**
  Same structure as IRIS, sent immediately upon connection:
  \`\`\`json
  {
    "gscid": "GREEK13",
    "gcid": "139",
    "sessionId": "$2a$10$JQJ.H4X.EtJNtAMLQ8tvnu",
    "device_type": "0",
    "isRestApi": "1"
  }
  \`\`\`
- **Subscription Frame Format:**
  Unlike login and heartbeats, subscription frames **must** be wrapped in a \`request\` envelope containing \`data\`, \`response_format\`, \`request_type\`, and \`streaming_type\`:
  \`\`\`json
  {
    "request": {
      "data": {
        "symbols": ["102044620", "102044621"]
      },
      "response_format": "json",
      "gscid": "GREEK13",
      "gcid": "139",
      "request_type": "subscribe",
      "streaming_type": "index"
    }
  }
  \`\`\`
  *Note:* The symbols list contains exchange tokens. For indices like NIFTY, tokens are mapped dynamically (e.g., \`101999957\` or \`101999895\`).
- **Heartbeat Payload:**
  Must be sent every 10 seconds:
  \`\`\`json
  {
    "streaming_type": "HeartBeat",
    "gcid": "139",
    "sessionId": "$2a$10$JQJ.H4X.EtJNtAMLQ8tvnu"
  }
  \`\`\`

---

## 5. Troubleshooting & Integration Checklist for Admins
When diagnosing connection failures or "down" statuses:
1. **Check System Status Sidebar:** Ensure "Trading Engine VPS" and "API Worker Cloudflare" show green.
2. **Verify IP Registration:** Verify with Greeksoft if the VPS IP \`80.225.242.244\` has access enabled for all ports (3001, 8228, 8229, 8230).
3. **Invalid Password (EC=2):** If \`jloginNew\` logs show \`ErrorCode=2\`, it means the trading/CTCL password for \`GREEK13\` is incorrect. Obtain the correct trading-level password from SVIS support and update it in the **Brokers** tab.
4. **WebSocket Closes (Code 1000) on Subscription:** This confirms Apollo is connected with a session that lacks market data rights. Ensure \`jloginNew\` succeeds before Apollo connects.
`

/* ── Exported Component ── */

export function GreeksoftApiContent() {
  return (
    <>
      {/* Hero */}
      <div className="kb-hero">
        <div className="kb-hero-glow" />
        <div className="kb-hero-content">
          <div className="kb-badge-row">
            <span className="kb-badge blue">API Reference</span>
            <span className="kb-badge green">Greeksoft GETS</span>
            <span className="kb-badge purple">16 Parts</span>
          </div>
          <h2 className="kb-hero-title">Greeksoft API Reference</h2>
          <p className="kb-hero-sub">Complete REST &amp; WebSocket API Documentation</p>
        </div>
      </div>

      {/* Rendered markdown content */}
      <section className="kb-sec">
        <div className="kb-prose">
          <style>{apiDocStyles}</style>
          <div
            className="api-doc"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(API_DOC_CONTENT) }}
          />
        </div>
      </section>
    </>
  )
}
